import { Group, Line, Mesh, MeshBasicMaterial, Sprite, SpriteMaterial, TextureLoader } from "three";
import { Text } from "troika-three-text";
import { RenderOrder } from "../RenderOrder";
import { busService } from "../../bus/BusService";
import { VrHudMessage } from "../../../messages/xr";
export class VrHud extends Group {
    constructor() {
        super();
        // Store HUD elements by name for easy access
        this.contents = new Map();
        // Bus connection for receiving and sending messages
        this.connection = busService.connection("VrHud");
        // Handle incoming VRHudMessage messages
        this.handleVrHudMessage = (msg) => {
            const oldText = this.contents.get(msg.topic);
            if (oldText !== undefined && oldText instanceof Mesh) {
                if (Array.isArray(oldText.material)) {
                    oldText.material.forEach(mat => mat.dispose());
                }
                else {
                    oldText.material.dispose();
                }
                this.remove(oldText);
                this.contents.delete("text");
            }
            const textOptions = {
                fontSize: 0.03,
                color: "white"
            };
            this.createText(msg.topic, msg.message, textOptions);
            this.flashElement(msg.topic, 'green', 1000, 3);
        };
        // Position HUD 0.5 meters in front of the camera
        this.position.set(0, 0, -1);
        this.connection.subscribeByType(VrHudMessage, this.handleVrHudMessage);
        // Initially hide the HUD
        this.visible = false;
    }
    removeElement(name) {
        const element = this.contents.get(name);
        if (element !== undefined) {
            if (element instanceof Sprite || element instanceof Mesh || element instanceof Line) {
                if (Array.isArray(element.material)) {
                    element.material.forEach(mat => mat.dispose());
                }
                else {
                    element.material.dispose();
                }
                this.remove(element);
                this.contents.delete(name);
            }
        }
    }
    /**
     * Adds a sprite to the HUD and stores it by name.
     */
    addNamedHudElement(name, object) {
        object.name = name;
        this.contents.set(name, object);
        this.add(object);
    }
    /**
     * Highlights a specific element by changing its color.
     * @param name The name of the sprite to highlight.
     * @param color The highlight color (e.g., 'yellow', '#FFFF00').
     * @param duration Duration in milliseconds for which the highlight should last.
     * @param numberOfFlashes The number of times the element should flash.
     */
    flashElement(name, color = 'yellow', duration, numberOfFlashes = 1) {
        const element = this.contents.get(name);
        if (!element || !(element instanceof Mesh || element instanceof Sprite || element instanceof Line) || !('material' in element) || !(element.material && 'color' in element.material)) {
            return;
        }
        const material = element.material;
        const originalColor = material.color.clone();
        const flashOn = (flashesLeft) => {
            if (flashesLeft <= 0) {
                // After the last flash, wait for the duration before finally turning it off.
                setTimeout(() => {
                    material.color.copy(originalColor);
                    material.needsUpdate = true;
                }, duration);
                return;
            }
            material.color.set(color);
            material.needsUpdate = true;
            setTimeout(() => { flashOff(flashesLeft); }, duration);
        };
        const flashOff = (flashesLeft) => {
            material.color.copy(originalColor);
            material.needsUpdate = true;
            setTimeout(() => { flashOn(flashesLeft - 1); }, duration);
        };
        flashOn(numberOfFlashes);
    }
    /**
     * Creates an image-based sprite
     */
    createImageSprite(name, path, scale, position) {
        const loader = new TextureLoader();
        const material = new SpriteMaterial({
            transparent: true,
            depthTest: false // Ensures HUD is always on top
        });
        const sprite = new Sprite(material);
        sprite.renderOrder = RenderOrder.HUD_IMAGE_ELEMENTS;
        sprite.position.set(position[0], position[1], position[2]);
        // Load texture and apply aspect ratio in the onLoad callback
        loader.load(path, (texture) => {
            material.map = texture;
            material.needsUpdate = true;
            const aspect = texture.image.width / texture.image.height;
            sprite.scale.set(scale[0] * aspect, scale[1], 1);
        });
        this.addNamedHudElement(name, sprite);
        return sprite;
    }
    /**
     * Creates a text-based sprite using a CanvasTexture
     */
    createText(name, text, options) {
        const textMesh = new Text();
        textMesh.gpuAccelerateSDF = true;
        textMesh.text = text;
        textMesh.fontSize = options.fontSize;
        textMesh.outlineColor = "black";
        textMesh.outlineWidth = 0.001;
        textMesh.color = options.color;
        textMesh.anchorX = 'center';
        textMesh.anchorY = 'middle';
        textMesh.material = new MeshBasicMaterial({ depthTest: false });
        textMesh.translateY(0.09);
        textMesh.sync();
        textMesh.renderOrder = RenderOrder.HUD_TEXT_ELEMENTS;
        this.addNamedHudElement(name, textMesh);
        return textMesh;
    }
}
